import type { NavigateFunction } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import type { Entity, EntityActionConfig } from '@/types/Entity';
import type { EntityTypeConfiguration } from '@/services/ConfigService';

export interface EntityActionNotifications {
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

export interface EntityActionContext {
  entity?: Entity;
  entityTypeConfig: EntityTypeConfiguration;
  queryClient: QueryClient;
  notifications?: EntityActionNotifications;
  /**
   * Optional navigation helper for actions that need to redirect.
   */
  navigate?: NavigateFunction;
  /**
   * URL search string to forward as location state so the destination page can
   * restore the caller's filter state on back navigation.
   */
  returnSearch?: string;
  /**
   * Optional modal/dialog controls – callers decide which identifiers to support.
   */
  openDialog?: <Payload = unknown>(dialogKey: string, payload?: Payload) => void;
  closeDialog?: (dialogKey: string) => void;
}

export type EntityActionHandler = (
  context: EntityActionContext,
  action: EntityActionConfig,
) => Promise<void> | void;

const actionRegistry = new Map<string, EntityActionHandler>();

export const registerEntityAction = (handlerId: string, handler: EntityActionHandler): void => {
  actionRegistry.set(handlerId, handler);
};

export const getEntityAction = (handlerId: string): EntityActionHandler | undefined => {
  return actionRegistry.get(handlerId);
};

export const registerEntityActions = (handlers: Record<string, EntityActionHandler>): void => {
  Object.entries(handlers).forEach(([handlerId, handler]) => {
    registerEntityAction(handlerId, handler);
  });
};
